import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { searchGeneralProducts } from "../api/aliApi";
import axios from "../api/axiosInstance";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    axios
      .get("/categories/hierarchy/")
      .then((res) => {
        const data = res?.data?.hierarchy || [];
        setCategories(data);
      })
      .catch((err) => console.error("Failed to fetch categories:", err));
  }, []);

  const getCategoryName = (catId) => {
    for (const parent of categories) {
      if (parent.category_id === catId) return parent.category_name;
      const match = parent.children?.find(
        (sub) => sub.category_id === catId
      );
      if (match) return match.category_name;
    }
    return "Products";
  };

  useEffect(() => {
    const initialSearch = searchParams.get("search") || "";
    const initialCategory = searchParams.get("category") || "";

    setKeyword(initialSearch);
    setCategoryId(initialCategory);
    setPageIndex(1);
    setProducts([]);
    setHasMore(true);
    fetchProducts(false, initialSearch, initialCategory);
  }, [searchParams]);

  const fetchProducts = async (
    append = false,
    customKeyword = keyword,
    customCategory = categoryId
  ) => {
    setLoading(true);
    try {
      const res = await searchGeneralProducts(
        customKeyword,
        customCategory,
        pageIndex
      );
      const newItems =
        res?.aliexpress_ds_text_search_response?.data?.products
          ?.selection_search_product || [];

      if (append) {
        setProducts((prev) => [...prev, ...newItems]);
        setPageIndex((prev) => prev + 1);
      } else {
        setProducts(newItems);
        setPageIndex(2);
      }

      if (newItems.length === 0) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPageIndex(1);
    setProducts([]);
    setHasMore(true);
    fetchProducts(false);
  };

  return (
    <>
      <Helmet>
        <title>
          {keyword
            ? `Search results for "${keyword}" – KambiHome Products`
            : categoryId
            ? `Explore ${getCategoryName(categoryId)} – KambiHome Products`
            : "Browse Global Products – KambiHome"}
        </title>
        <meta
          name="description"
          content={
            keyword
              ? `Find "${keyword}" products through KambiHome's connected global supply experience.`
              : categoryId
              ? `Discover products in the ${getCategoryName(categoryId)} category on KambiHome.`
              : "Browse home, lifestyle, and selected global products through KambiHome's connected supply corridors."
          }
        />
        <link rel="canonical" href={window.location.href} />
        <meta
          property="og:title"
          content={
            keyword
              ? `Search: ${keyword} – KambiHome`
              : categoryId
              ? `Category: ${getCategoryName(categoryId)} – KambiHome`
              : "Explore Global Products – KambiHome"
          }
        />
        <meta
          property="og:description"
          content={
            keyword
              ? `Explore products related to "${keyword}" on KambiHome.`
              : categoryId
              ? `Explore products in ${getCategoryName(categoryId)}.`
              : "Discover products available through KambiHome's connected global supply experience."
          }
        />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <section className="py-12 px-4 mt-16 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-7">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-green-700 mb-2">
              Global Supply • Commerce • Procurement
            </p>
            <h1 className="text-3xl font-bold text-gray-800">
              Explore Products
            </h1>
          </div>

          <form
            onSubmit={handleSearch}
            className="mb-8 flex flex-col md:flex-row items-center justify-center gap-4"
          >
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search products..."
              className="border px-4 py-2 rounded-md w-full md:w-1/2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />

            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-1/3 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">All Categories</option>
              {categories.map((parent) => (
                <optgroup
                  key={parent.category_id}
                  label={parent.category_name}
                >
                  {parent.children?.map((sub) => (
                    <option key={sub.category_id} value={sub.category_id}>
                      {sub.category_name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>

            <button
              type="submit"
              className="cursor-pointer bg-green-600 text-white px-5 py-2 rounded-md hover:bg-green-700 hover:scale-105 transition-all duration-300"
            >
              Search
            </button>
          </form>

          {loading && products.length === 0 ? (
            <p className="text-center text-gray-500 animate-pulse">
              Loading products...
            </p>
          ) : products.length === 0 ? (
            <p className="text-center text-gray-500">No products found.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {products.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 p-4 flex flex-col"
                >
                  <img
                    src={item.itemMainPic || "https://placehold.co/300x300"}
                    alt={item.title}
                    className="w-full h-48 object-cover rounded-md mb-4"
                  />
                  <h4 className="text-sm font-medium text-gray-800 line-clamp-2">
                    {item.title}
                  </h4>
                  <p className="text-green-600 mt-2 font-semibold text-sm">
                    {item.salePriceFormat ||
                      `₦${parseInt(
                        item.targetSalePrice || 0
                      ).toLocaleString()}`}
                  </p>
                  <Link
                    to={`/aliproduct/${item.itemId}`}
                    className="cursor-pointer mt-3 inline-block text-center text-sm font-semibold text-white bg-green-600 hover:bg-green-700 hover:scale-105 transition-all duration-300 rounded-md py-2"
                  >
                    View Details
                  </Link>
                </div>
              ))}
            </div>
          )}

          {hasMore && !loading && products.length > 0 && (
            <div className="mt-10 text-center">
              <button
                onClick={() => fetchProducts(true)}
                className="cursor-pointer px-6 py-3 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 hover:scale-105 transition-all duration-300"
              >
                Load More
              </button>
            </div>
          )}

          {loading && products.length > 0 && (
            <p className="text-center text-gray-500 mt-4 animate-pulse">
              Loading more...
            </p>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ProductsPage;
